'use client'

// The Character.AI importer (src/app/character-ai-import): screenshots in, a Promptchan character out.
//
//   1. Pick:   1-5 screenshots (pick, drop or paste), shrunk in the browser. Anyone can get this far.
//   2. Read:   signed in, they go to POST /api/cai_import/read. A guest's screenshots wait in this browser
//              (IndexedDB) while they make an account; sign-up brings them back here (src/lib/return-to.js).
//   3. Review: every field editable; the portrait is generated from scratch from the editable prompt (text to
//              image through /api/generate, the settings the backend chose), then POST /api/cai_import/create.
//
// Nothing here decides anything: what is refused, the free portrait and the limits are the backend's
// (promptchan-backend myapp/cai_import.py), and the page shows its answers.
import { getIdToken } from 'firebase/auth'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../../app/(chatAndCreateContext)/generate/api'
import { imageRequest } from '../../app/(chatAndCreateContext)/generate/requests'
import { useAuth } from '../../contexts/AuthContext'
import { safeSetItem } from '../../helpers/safeStorage'
import {
  LIMITS,
  MAX_SHOTS,
  blobToBase64,
  clearPendingShots,
  createBody,
  importErrorMessage,
  loadPendingShots,
  portraitSettings,
  savePendingShots,
  shrinkScreenshot,
} from '../../lib/cai-import'
import { setReturnTo } from '../../lib/return-to'

const CLOUD = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD
const EXAMPLE = '/assets/cai-import/example-edit.webp'

const asImage = (src) =>
  src.startsWith('data:') || src.startsWith('http')
    ? src
    : `data:image/jpeg;base64,${src}`

async function post(path, token, body) {
  const res = await fetch(`${CLOUD}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  })
  return { status: res.status, body: await res.json().catch(() => ({})) }
}

function Field({ label, name, value, onChange, filled, rows }) {
  const limit = LIMITS[name]
  const Input = rows ? 'textarea' : 'input'
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center justify-between text-sm text-gray-300">
        <span>
          {label}
          {filled && (
            <span className="ml-2 rounded-full bg-fuchsia-500/15 px-2 py-0.5 text-xs text-fuchsia-200">
              filled in by AI
            </span>
          )}
        </span>
        <span className="text-xs text-gray-500">
          {value.length}/{limit}
        </span>
      </span>
      <Input
        name={name}
        rows={rows}
        maxLength={limit}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-white outline-none focus:border-fuchsia-400"
      />
    </label>
  )
}

function Example() {
  return (
    <figure className="rounded-3xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
      <figcaption className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <span className="rounded-full bg-amber-400 px-2 py-0.5 text-xs font-bold text-black">
          EXAMPLE
        </span>
        The ideal screenshot
      </figcaption>
      <div className="relative mx-auto w-full max-w-[260px] overflow-hidden rounded-2xl border border-white/10">
        <Image
          src={EXAMPLE}
          alt="Example: a Character.AI Edit Character screen showing the name, tagline, description, greeting and definition of a made-up character"
          width={860}
          height={2018}
          sizes="260px"
          className="h-auto w-full"
        />
      </div>
      <ol className="mt-4 space-y-1.5 text-sm text-gray-300">
        <li>
          1. On Character.AI, open your character and tap <b>Edit</b>.
        </li>
        <li>
          2. Open <b>Advanced</b> so the <b>Definition</b> shows.
        </li>
        <li>3. Screenshot it. Long? Take two and add both.</li>
        <li>4. Optional: add a chat screenshot to carry your story over.</li>
      </ol>
      <p className="mt-3 text-xs text-gray-500">
        Not your character? Its profile page works too; we only see what it
        shows.
      </p>
    </figure>
  )
}

export default function Importer() {
  const router = useRouter()
  const { user, isPlusOrHigher, subscription } = useAuth()
  const [shots, setShots] = useState([]) // [{ blob, url }]
  const [restored, setRestored] = useState(false)
  const [step, setStep] = useState('pick') // pick | reading | review | creating
  const [error, setError] = useState('')
  const [fields, setFields] = useState(null)
  const [filled, setFilled] = useState([])
  const [portrait, setPortrait] = useState(null) // the backend's settings
  const [granted, setGranted] = useState(0)
  const [portraits, setPortraits] = useState([]) // base64 strings, newest first
  const [chosen, setChosen] = useState(0)
  const [generating, setGenerating] = useState(false)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef(null)

  const addFiles = useCallback(async (files) => {
    const images = [...files].filter((f) => f.type.startsWith('image/'))
    if (!images.length) return
    setError('')
    const shrunk = await Promise.all(
      images.map((f) => shrinkScreenshot(f).catch(() => null)),
    )
    setShots((prev) =>
      [
        ...prev,
        ...shrunk
          .filter(Boolean)
          .map((blob) => ({ blob, url: URL.createObjectURL(blob) })),
      ].slice(0, MAX_SHOTS),
    )
  }, [])

  // Screenshots kept from before sign-up (or a reload) come back, ready to read.
  useEffect(() => {
    let live = true
    loadPendingShots().then((blobs) => {
      if (!live || !blobs.length) return
      setShots(blobs.map((blob) => ({ blob, url: URL.createObjectURL(blob) })))
      setRestored(true)
    })
    return () => {
      live = false
    }
  }, [])

  // Paste a screenshot straight from the clipboard.
  useEffect(() => {
    if (step !== 'pick') return
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])]
      if (files.length) addFiles(files)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [step, addFiles])

  const removeShot = (i) =>
    setShots((prev) => prev.filter((_, index) => index !== i))

  const read = async () => {
    if (!shots.length) return
    if (!user) {
      await savePendingShots(shots.map((s) => s.blob))
      setReturnTo('/character-ai-import')
      router.push('/signup')
      return
    }
    setStep('reading')
    setError('')
    try {
      const images = await Promise.all(shots.map((s) => blobToBase64(s.blob)))
      const { status, body } = await post(
        '/api/cai_import/read',
        await getIdToken(user),
        { images },
      )
      if (status !== 200) {
        setError(importErrorMessage(status, body))
        setStep('pick')
        return
      }
      clearPendingShots()
      const { filled_in: filledIn = [], ...character } = body.character
      setFields(character)
      setFilled(filledIn)
      setPortrait(body.portrait)
      setGranted(body.gems_granted || 0)
      setPortraits([])
      setStep('review')
    } catch {
      setError(importErrorMessage(0))
      setStep('pick')
    }
  }

  const change = (name, value) => setFields((f) => ({ ...f, [name]: value }))

  const generate = async () => {
    setGenerating(true)
    setError('')
    try {
      const token = await getIdToken(user)
      const settings = portraitSettings({
        portrait,
        prompt: fields.portrait_prompt,
        plan: subscription?.plan,
        seed: Math.floor(Math.random() * 2 ** 31),
        jobId: crypto.randomUUID(),
      })
      const base = api.imageBackend({
        paid: isPlusOrHigher,
        withCharacter: false,
      })
      let res = await api.generateImage(
        base,
        settings.model,
        token,
        imageRequest(settings).body,
      )
      let body = await res.json().catch(() => ({}))
      if (res.status === 452 && body.fallback_model) {
        // A cold model: render on the warm one it offers, as the Create page does.
        const warm = { ...settings, model: body.fallback_model }
        res = await api.generateImage(
          base,
          warm.model,
          token,
          imageRequest(warm).body,
        )
        body = await res.json().catch(() => ({}))
      }
      const image = res.ok && body.images?.[0]
      if (!image) {
        setError(importErrorMessage(res.status, body))
        return
      }
      setPortraits((prev) => [image, ...prev].slice(0, 4))
      setChosen(0)
    } catch {
      setError(importErrorMessage(0))
    } finally {
      setGenerating(false)
    }
  }

  const create = async () => {
    setStep('creating')
    setError('')
    try {
      const { status, body } = await post(
        '/api/cai_import/create',
        await getIdToken(user),
        createBody(fields, portraits[chosen]),
      )
      if (status !== 200 || !body.id) {
        setError(importErrorMessage(status, body))
        setStep('review')
        return
      }
      safeSetItem('characterId', body.id)
      router.push('/chat')
    } catch {
      setError(importErrorMessage(0))
      setStep('review')
    }
  }

  const errorBox = error && (
    <p
      role="alert"
      className="mb-5 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200"
    >
      {error}
    </p>
  )

  if (step === 'review' || step === 'creating') {
    const ready = portraits.length > 0 && fields.name.trim()
    return (
      <section aria-label="Review your character" className="text-white">
        {errorBox}
        <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Check your character</h2>
            <p className="text-sm text-gray-400">
              Everything is editable. Fields marked “filled in by AI” weren’t in
              your screenshots.
            </p>
            <Field
              label="Name"
              name="name"
              value={fields.name}
              onChange={change}
              filled={filled.includes('name')}
            />
            <Field
              label="Personality"
              name="personality"
              rows={4}
              value={fields.personality}
              onChange={change}
              filled={filled.includes('personality')}
            />
            <Field
              label="Scenario"
              name="scenario"
              rows={4}
              value={fields.scenario}
              onChange={change}
              filled={filled.includes('scenario')}
            />
            <Field
              label="First message"
              name="first_message"
              rows={4}
              value={fields.first_message}
              onChange={change}
              filled={filled.includes('first_message')}
            />
            <div className="grid grid-cols-3 gap-3 text-sm">
              <label className="block">
                <span className="mb-1.5 block text-gray-300">Age</span>
                <input
                  type="number"
                  min={18}
                  max={80}
                  value={fields.age}
                  onChange={(e) => change('age', e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5"
                />
              </label>
              {[
                ['gender', 'Gender', ['Woman', 'Man']],
                ['sexuality', 'Sexuality', ['Straight', 'Gay', 'Lesbian']],
              ].map(([name, label, options]) => (
                <label key={name} className="block">
                  <span className="mb-1.5 block text-gray-300">{label}</span>
                  <select
                    value={fields[name]}
                    onChange={(e) => change(name, e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-[#111] px-3 py-2.5"
                  >
                    {options.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-bold">The portrait</h2>
            <Field
              label="Portrait prompt"
              name="portrait_prompt"
              rows={5}
              value={fields.portrait_prompt}
              onChange={change}
            />
            <p className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-xs leading-relaxed text-gray-400">
              Generated from scratch from the text above. We never use your
              screenshots, or any image, to make it.
            </p>
            <button
              type="button"
              onClick={generate}
              disabled={generating || !fields.portrait_prompt.trim()}
              data-track={
                portraits.length
                  ? 'cai_import.regenerate'
                  : 'cai_import.generate'
              }
              className="w-full rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 px-5 py-3 font-bold disabled:opacity-50"
            >
              {generating
                ? 'Generating…'
                : portraits.length
                  ? 'Generate another (2 Gems)'
                  : granted
                    ? 'Generate portrait (free)'
                    : 'Generate portrait'}
            </button>
            {portraits.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {portraits.map((src, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setChosen(i)}
                    aria-pressed={chosen === i}
                    className={`overflow-hidden rounded-2xl border-2 ${chosen === i ? 'border-fuchsia-400' : 'border-transparent'}`}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={asImage(src)}
                      alt={`Portrait option ${i + 1}`}
                      className="aspect-[2/3] w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={create}
              disabled={!ready || step === 'creating'}
              data-track="cai_import.create"
              className="w-full rounded-full bg-white px-5 py-3 font-bold text-black disabled:opacity-40"
            >
              {step === 'creating'
                ? 'Creating…'
                : 'Create and start chatting →'}
            </button>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section aria-label="Upload your screenshots" className="text-white">
      {errorBox}
      {restored && user && (
        <p className="mb-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
          Welcome back. Your screenshots are ready: press “Read my character”.
        </p>
      )}
      <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
        <div>
          <label
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              addFiles(e.dataTransfer.files)
            }}
            data-track="cai_import.pick"
            className={`flex min-h-[260px] cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed p-8 text-center transition ${
              dragging
                ? 'border-fuchsia-400 bg-fuchsia-500/10'
                : 'border-white/20 bg-white/[0.03] hover:border-fuchsia-400/60'
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              className="sr-only"
              onChange={(e) => {
                addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            <span className="mb-3 text-5xl" aria-hidden="true">
              📸
            </span>
            <span className="text-xl font-bold">
              Drop your Character.AI screenshots here
            </span>
            <span className="mt-1 text-sm text-gray-400">
              or tap to choose · paste works too · up to {MAX_SHOTS} images
            </span>
          </label>

          {shots.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-3">
              {shots.map((s, i) => (
                <li key={s.url} className="relative">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={s.url}
                    alt={`Screenshot ${i + 1}`}
                    className="h-28 w-20 rounded-xl object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeShot(i)}
                    aria-label={`Remove screenshot ${i + 1}`}
                    className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-black text-sm"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={read}
            disabled={!shots.length || step === 'reading'}
            data-track={user ? 'cai_import.read' : 'cai_import.signup'}
            className="mt-6 w-full rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 px-6 py-4 text-lg font-bold disabled:opacity-40 sm:w-auto"
          >
            {step === 'reading'
              ? 'Reading your character…'
              : user
                ? 'Read my character →'
                : 'Create a free account to continue →'}
          </button>
          <p className="mt-3 text-xs text-gray-500">
            {user
              ? 'Your screenshots are read once and never stored.'
              : 'Free. Your screenshots stay in this browser until your account is ready, then come straight back here.'}{' '}
            Original characters of adults only: no real people, no characters
            from shows, games or anime. Prefer to start fresh?{' '}
            <Link href="/chat" className="underline">
              Browse companions
            </Link>
            .
          </p>
        </div>
        <Example />
      </div>
    </section>
  )
}
